import { useEffect, useMemo, useState } from 'react';
import * as Draggable from '@givebutter/primitives/draggable';
import { useFiltersByKey } from 'components/filter/filters-next/useFiltersByKey';
import { AddTaskDialog } from 'components/tasks/AddTaskDialog';
import { taskFiltersToRequest } from 'components/tasks/form/upstream';
import { ArchivedTaskGroup } from 'components/tasks/list/ArchivedTaskGroup';
import * as TaskCards from 'components/tasks/list/TaskCards';
import * as TaskGroup from 'components/tasks/list/TaskGroup';
import { useTaskDrawerState } from 'components/tasks/useTaskDrawerState';
import {
    findSortableContainer,
    transformTasksToGroups,
} from 'components/tasks/utils/sortableHelpers';
import { taskStatuses } from 'components/tasks/utils/useTaskForm';
import { useTasks } from 'components/tasks/utils/useTasks';
import {
    taskFilterValuesSelector,
    useTasksFilterStore,
} from 'components/tasks/utils/useTasksFilterStore';
import { useUpdateTask } from 'components/tasks/utils/useUpdateTask';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { orderBy } from 'lodash';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { TaskStatus } from 'types/domain';

const TasksList = () => {
    const { accountId, timezone } = useCurrentAccount();
    const { setSelectedTaskId } = useTaskDrawerState();

    const { query, sortBy, sortByDesc } = useTasksFilterStore(taskFilterValuesSelector);
    const { tasks, meta, refs } = useTasks();

    const { filters, filterGroups } = useFiltersByKey('tasks');

    const [items, setItems] = useState(transformTasksToGroups(tasks));
    useEffect(() => {
        setItems(transformTasksToGroups(tasks));
        for (const task of tasks) {
            queryClient.setQueryData(accountQueryKeys.taskById(accountId, task.id), task);
            queryClient.setQueryData(
                accountQueryKeys.taskIdsWithFilters(
                    accountId,
                    taskFiltersToRequest({ query, sortBy, sortByDesc }),
                ),
                tasks.map(t => t.id),
            );
        }
    }, [tasks, accountId, query, sortBy, sortByDesc, timezone]);

    const [activeId, setActiveId] = useState<Draggable.UniqueIdentifier | null>(null);
    const activeTask = tasks.find(t => t.id === activeId);

    const { mutate: updateTask } = useUpdateTask();

    const isFilterApplied = useMemo(() => Boolean(query) || filters.length > 0, [query, filters]);
    const isArchivedSelected = filterGroups.find(f =>
        f.filters.find(f => f.serverKey === 'archived'),
    );

    return (
        <Draggable.Root
            onDragStart={({ active }) => setActiveId(active.id || null)}
            onDragEnd={({ active, over }) => {
                if (!active.id || !over?.id) return;

                const task = tasks.find(t => t.id === active.id);
                const overContainer = findSortableContainer(over.id, items);

                if (!task || !overContainer || task.status === overContainer) return;

                updateTask({
                    taskId: active.id as number,
                    params: { status: overContainer as TaskStatus },
                });
            }}
            onDragOver={({ active, over }) => {
                if (!active.id || !over?.id) return;
                const activeContainer = findSortableContainer(active.id, items);
                const overContainer = findSortableContainer(over.id, items);

                if (!activeContainer || !overContainer || activeContainer === overContainer) return;
                setItems(items => {
                    const newActiveItems = items[activeContainer].filter(i => i !== active.id);
                    let newOverItems = [...items[overContainer], active.id];
                    const overTasks = tasks.filter(t => newOverItems.includes(t.id));
                    let sortedOverTasks: Draggable.UniqueIdentifier[] = [];

                    if (sortBy) {
                        sortedOverTasks = orderBy(overTasks, sortBy, 'asc').map(t => t.id);
                    }

                    if (sortByDesc) {
                        sortedOverTasks = orderBy(overTasks, sortByDesc, 'desc').map(t => t.id);
                    }

                    return {
                        ...items,
                        [activeContainer]: newActiveItems,
                        [overContainer]: sortedOverTasks,
                    };
                });
            }}
        >
            {isArchivedSelected ? (
                <ArchivedTaskGroup />
            ) : (
                taskStatuses.map(({ id, label }) => {
                    const foundItems = items[id] || [];
                    return (
                        <TaskGroup.Root key={id} id={id}>
                            <TaskGroup.Container>
                                <TaskGroup.Header>
                                    <div className="flex items-center gap-2">
                                        <TaskGroup.CollapseTrigger />
                                        <TaskGroup.Title>{label}</TaskGroup.Title>
                                        <TaskGroup.Count>{meta[id]?.total}</TaskGroup.Count>
                                    </div>
                                    <AddTaskDialog defaultStatus={id}>
                                        <TaskGroup.AddAction />
                                    </AddTaskDialog>
                                </TaskGroup.Header>

                                <TaskGroup.Content>
                                    {tasks.length > 0 && (
                                        <TaskCards.Root>
                                            <ul className="flex w-full flex-col gap-4 px-8">
                                                {foundItems.map(id => {
                                                    const fullTask = tasks.find(t => t.id === id);
                                                    if (!fullTask) return null;
                                                    return (
                                                        <TaskCards.TaskCard
                                                            key={fullTask.id}
                                                            task={fullTask}
                                                            onNavigate={() => {
                                                                setSelectedTaskId(fullTask.id);
                                                            }}
                                                        />
                                                    );
                                                })}
                                            </ul>
                                        </TaskCards.Root>
                                    )}

                                    {tasks.length === 0 &&
                                        id === 'todo' &&
                                        (isFilterApplied ? (
                                            <TaskGroup.NotFound />
                                        ) : (
                                            <TaskGroup.Empty />
                                        ))}

                                    {!(id === 'todo' && tasks.length === 0 && !isFilterApplied) && (
                                        <div className="px-8">
                                            <AddTaskDialog defaultStatus={id}>
                                                <TaskGroup.AddAction variant="text" />
                                            </AddTaskDialog>
                                        </div>
                                    )}

                                    <div ref={refs[id]} />
                                </TaskGroup.Content>
                            </TaskGroup.Container>
                        </TaskGroup.Root>
                    );
                })
            )}
            <Draggable.Overlay>
                {activeId && activeTask ? <TaskCards.TaskDraggingCard task={activeTask} /> : null}
            </Draggable.Overlay>
        </Draggable.Root>
    );
};

export { TasksList };
